import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { ShieldCheck, Plus, TriangleAlert, Copy, Check, Clock, X, Activity as ActivityIcon, Truck, RotateCcw, TrendingUp } from "lucide-react";

const EMPTY_FORM = { name: "", email: "", role_id: "" };

const APPROVAL_META = {
  active: null,
  pending: { label: "Pending approval", cls: "bg-amber-50 text-amber-700" },
  rejected: { label: "Rejected", cls: "bg-rose-50 text-rose-700" },
};

const ACTION_LABEL = {
  address_edit: "Address edited",
  cancel: "Order cancelled",
  refund: "Refund issued",
};

export default function AdminStaffPage() {
  const { user, can } = useAuth();
  const isAdmin = can("staff.manage");
  const canCreate = isAdmin || can("staff.create");
  const canViewList = isAdmin || can("staff.view") || canCreate;
  const canViewOthersActivity = isAdmin || can("staff.view");

  const [staff, setStaff] = useState([]);
  const [roles, setRoles] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [pendingQueue, setPendingQueue] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [activityTarget, setActivityTarget] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const calls = [api.get("/admin/staff"), api.get("/admin/roles")];
      if (isAdmin) {
        calls.push(api.get("/admin/staff/unassigned"));
        calls.push(api.get("/admin/staff/pending"));
      }
      const results = await Promise.all(calls);
      setStaff(results[0].data);
      setRoles(results[1].data);
      if (isAdmin) {
        setUnassigned(results[2].data);
        setPendingQueue(results[3].data);
      }
    } catch (e) {
      toast.error("Couldn't load staff");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [isAdmin]);

  const isAssignable = (r) => !r.is_protected && (r.status || "active") === "active";
  const assignableRoles = roles.filter(isAssignable);
  const roleOptionsForChange = (currentRoleId) => roles.filter((r) => isAssignable(r) || (r.id === "admin" && user?.role === "admin"));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setCreateResult(null);
    setCopied(false);
    setOpen(true);
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.role_id) {
      toast.error("Fill in name, email, and role");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/staff", form);
      if (data.status === "pending") {
        toast.success("Submitted for Admin approval");
        setCreateResult({ email: data.email, status: "pending" });
      } else if (data.email_sent) {
        toast.success(`Login details emailed to ${data.email}`);
        setOpen(false);
      } else {
        setCreateResult({ email: data.email, status: "active", temp_password: data.temp_password });
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't create staff account");
    } finally {
      setCreating(false);
    }
  };

  const changeRole = async (staffId, currentRoleId, newRoleId, memberName) => {
    const involvesAdmin = currentRoleId === "admin" || newRoleId === "admin";
    if (involvesAdmin) {
      const message = newRoleId === "admin"
        ? `Give ${memberName} full Admin access? They'll be able to manage all roles, staff, and see Net Margin.`
        : `Remove Admin access from ${memberName}? They'll be reassigned to ${newRoleId.replace("_", " ")}.`;
      if (!window.confirm(message)) return;
    }
    try {
      await api.patch(`/admin/staff/${staffId}/role`, null, { params: { role_id: newRoleId } });
      toast.success("Role updated");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update role");
    }
  };

  const deactivate = async (member) => {
    if (!window.confirm(`Deactivate ${member.name}? They'll lose access immediately, including any active session.`)) return;
    try {
      await api.patch(`/admin/staff/${member.id}/deactivate`);
      toast.success(`${member.name} deactivated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't deactivate this account");
    }
  };

  const reactivate = async (member) => {
    try {
      await api.patch(`/admin/staff/${member.id}/reactivate`);
      toast.success(`${member.name} reactivated`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reactivate this account");
    }
  };

  const approveStaff = async (member) => {
    try {
      const { data } = await api.post(`/admin/staff/${member.id}/approve`);
      toast.success(data.email_sent ? `${member.name} approved — login details emailed` : `${member.name} approved`);
      if (!data.email_sent) {
        setCreateResult({ email: data.email, status: "active", temp_password: data.temp_password });
        setOpen(true);
      }
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't approve");
    }
  };

  const openReject = (member) => {
    setRejectTarget(member);
    setRejectReason("");
  };

  const submitReject = async () => {
    try {
      await api.post(`/admin/staff/${rejectTarget.id}/reject`, { reason: rejectReason.trim() || null });
      toast.success(`${rejectTarget.name} rejected`);
      setRejectTarget(null);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't reject");
    }
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(createResult.temp_password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Anyone can view their own activity. Viewing someone else's requires
  // staff.view or staff.manage — matches the backend's exact rule.
  const canViewActivity = (member) => member.id === user?.id || canViewOthersActivity;

  const openActivity = async (member) => {
    setActivityTarget(member);
    setActivityData(null);
    setLoadingActivity(true);
    try {
      const { data } = await api.get(`/admin/staff/${member.id}/activity`);
      setActivityData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load activity");
      setActivityTarget(null);
    } finally {
      setLoadingActivity(false);
    }
  };

  if (!canViewList) {
    return <div className="text-sm text-welljio-muted" data-testid="staff-no-access">You don't have access to this page.</div>;
  }

  return (
    <div data-testid="admin-staff-page" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-welljio-subtle mb-2">Access control</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Staff</h1>
        </div>
        {canCreate && (
          <button
            data-testid="staff-create-button"
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-welljio-ink text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-slate-800"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> Add staff member
          </button>
        )}
      </div>

      {isAdmin && unassigned.length > 0 && (
        <div data-testid="unassigned-staff-banner" className="flex items-center gap-3 bg-welljio-coralSoft text-welljio-coral rounded-2xl px-4 py-3 text-sm">
          <TriangleAlert className="w-4 h-4 shrink-0" strokeWidth={1.5} />
          {unassigned.length} staff {unassigned.length === 1 ? "account has" : "accounts have"} no role assigned since a role they held was deleted.
        </div>
      )}

      {isAdmin && pendingQueue.length > 0 && (
        <div data-testid="pending-staff-queue" className="bg-amber-50 border border-amber-200 rounded-3xl p-5 space-y-3">
          <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
            <Clock className="w-4 h-4" strokeWidth={1.8} />
            {pendingQueue.length} staff {pendingQueue.length === 1 ? "account" : "accounts"} waiting for your approval
          </p>
          {pendingQueue.map((member) => (
            <div key={member.id} data-testid={`pending-staff-${member.id}`} className="bg-white border border-amber-100 rounded-2xl p-4 flex items-center justify-between gap-3">
              <div>
                <p className="font-medium text-welljio-ink text-sm">{member.name}</p>
                <p className="text-xs text-welljio-subtle">{member.email} &middot; requested as {member.role}</p>
                <p className="text-xs text-welljio-subtle">
                  Submitted by {member.submitted_by} &middot; {member.submitted_at && new Date(member.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => approveStaff(member)}
                  data-testid={`approve-staff-${member.id}`}
                  className="inline-flex items-center gap-1.5 bg-welljio-ink text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-slate-800"
                >
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button
                  onClick={() => openReject(member)}
                  data-testid={`reject-staff-${member.id}`}
                  className="inline-flex items-center gap-1.5 border border-welljio-coralSoft text-welljio-coral rounded-full px-4 py-2 text-xs font-medium hover:bg-welljio-coralSoft"
                >
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white border border-slate-100 rounded-3xl divide-y divide-slate-100">
        {loading ? (
          <div className="px-5 py-6 text-sm text-welljio-muted">Loading staff...</div>
        ) : staff.length === 0 ? (
          <div className="px-5 py-6 text-sm text-welljio-muted" data-testid="staff-empty-state">No staff accounts yet.</div>
        ) : (
          staff.map((member) => {
            const isActive = member.is_active !== false;
            const isSelf = member.id === user?.id;
            const approvalMeta = APPROVAL_META[member.approval_status || "active"];
            const canManageThisRow = isAdmin && !isSelf;
            return (
              <div key={member.id} data-testid={`staff-row-${member.id}`} className={`flex items-center justify-between px-5 py-4 ${!isActive ? "opacity-50" : ""}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-welljio-tint flex items-center justify-center text-welljio-accent">
                    <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-welljio-ink text-sm">{member.name}</p>
                      {approvalMeta && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${approvalMeta.cls}`}>{approvalMeta.label}</span>}
                    </div>
                    <p className="text-xs text-welljio-subtle">{member.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <span className={`text-[11px] font-semibold px-3 py-1 rounded-full ${isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {isActive ? "Active" : "Deactivated"}
                  </span>

                  {isSelf ? (
                    <span className="text-xs font-medium text-welljio-subtle px-3 py-1.5">{member.role_name || member.role} (you)</span>
                  ) : canManageThisRow ? (
                    <select
                      data-testid={`staff-role-select-${member.id}`}
                      value={member.role || ""}
                      onChange={(e) => changeRole(member.id, member.role, e.target.value, member.name)}
                      disabled={!isActive}
                      className="rounded-full border border-slate-200 px-3 py-1.5 text-xs bg-white disabled:opacity-50"
                    >
                      {roleOptionsForChange(member.role).map((r) => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="text-xs font-medium text-welljio-muted px-3 py-1.5">{member.role_name || member.role}</span>
                  )}

                  {canViewActivity(member) && (
                    <button
                      onClick={() => openActivity(member)}
                      data-testid={`staff-activity-${member.id}`}
                      className="inline-flex items-center gap-1.5 text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-welljio-tint text-welljio-muted"
                    >
                      <ActivityIcon className="w-3 h-3" strokeWidth={1.8} /> Activity
                    </button>
                  )}

                  {canManageThisRow && (
                    isActive ? (
                      <button
                        onClick={() => deactivate(member)}
                        data-testid={`staff-deactivate-${member.id}`}
                        className="text-xs rounded-full border border-welljio-coralSoft text-welljio-coral px-3 py-1.5 hover:bg-welljio-coralSoft"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => reactivate(member)}
                        data-testid={`staff-reactivate-${member.id}`}
                        className="text-xs rounded-full border border-slate-200 px-3 py-1.5 hover:bg-welljio-tint"
                      >
                        Reactivate
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-create-dialog" className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {createResult?.status === "active" && createResult?.temp_password ? "Login details" : "Add staff member"}
            </DialogTitle>
          </DialogHeader>

          {createResult?.status === "pending" ? (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                <p className="text-xs font-semibold text-amber-800 mb-1">Submitted for approval</p>
                <p className="text-xs text-welljio-muted">
                  {createResult.email} has been added, but won't be able to log in until Admin reviews and approves the request.
                </p>
              </div>
              <button
                onClick={() => { setOpen(false); setCreateResult(null); }}
                data-testid="staff-create-done"
                className="w-full bg-welljio-ink text-white rounded-full py-3.5 font-medium hover:bg-slate-800"
              >
                Done
              </button>
            </div>
          ) : createResult?.temp_password ? (
            <div className="space-y-4">
              <div className="bg-welljio-coralSoft border border-welljio-coral/20 rounded-2xl p-4">
                <p className="text-xs font-semibold text-welljio-coral mb-1">Email couldn't be sent</p>
                <p className="text-xs text-welljio-muted mb-3">
                  SMTP isn't configured yet — share this temporary password with {createResult.email} directly.
                </p>
                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2.5">
                  <code data-testid="staff-temp-password" className="flex-1 font-mono text-sm font-semibold">{createResult.temp_password}</code>
                  <button onClick={copyPassword} data-testid="staff-copy-password" className="text-xs font-medium text-welljio-accent bg-welljio-highlight rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1">
                    {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
              </div>
              <button
                onClick={() => { setOpen(false); setCreateResult(null); }}
                data-testid="staff-create-done"
                className="w-full bg-welljio-ink text-white rounded-full py-3.5 font-medium hover:bg-slate-800"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={submitCreate} className="space-y-3 mt-2">
              {!isAdmin && (
                <p className="text-xs text-welljio-coral bg-welljio-coralSoft rounded-2xl px-4 py-3">
                  New staff accounts need Admin approval before they can log in.
                </p>
              )}
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Full name"
                data-testid="staff-form-name"
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"
              />
              <input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="Email"
                data-testid="staff-form-email"
                className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"
              />
              <div className="space-y-2 pt-1">
                <label className="text-xs uppercase tracking-widest text-welljio-subtle">Assign role</label>
                {assignableRoles.map((r) => (
                  <label key={r.id} className={`flex items-center gap-3 border rounded-2xl px-4 py-3 text-sm cursor-pointer ${form.role_id === r.id ? "border-welljio-ink bg-welljio-tint font-medium" : "border-slate-200"}`}>
                    <input
                      type="radio"
                      name="role_id"
                      value={r.id}
                      checked={form.role_id === r.id}
                      onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                      className="accent-welljio-ink"
                    />
                    {r.name}
                  </label>
                ))}
              </div>
              <button
                type="submit"
                disabled={creating}
                data-testid="staff-form-submit"
                className="w-full bg-welljio-ink text-white rounded-full py-3.5 font-medium hover:bg-slate-800 disabled:opacity-60"
              >
                {creating ? "Submitting..." : !isAdmin ? "Submit for approval" : "Create account & send password"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent data-testid="reject-staff-dialog" className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Reject {rejectTarget?.name}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-welljio-muted">This account stays on record as rejected and will never be able to log in.</p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason (optional)"
            rows={3}
            data-testid="reject-staff-reason"
            className="w-full border border-slate-200 rounded-2xl px-4 py-3 text-sm outline-none focus:border-welljio-accent"
          />
          <div className="flex items-center gap-3">
            <button onClick={submitReject} data-testid="reject-staff-submit" className="bg-red-600 text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-red-700">
              Reject
            </button>
            <button onClick={() => setRejectTarget(null)} className="text-welljio-muted text-sm px-4">Cancel</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Activity panel — raw counts and timestamps only, no scoring or
          ranking. Pulls from actor-tracked status_history, inventory
          return events, and the existing order_activity_log. */}
      <Dialog open={!!activityTarget} onOpenChange={(o) => { if (!o) setActivityTarget(null); }}>
        <DialogContent data-testid="staff-activity-dialog" className="max-w-lg bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{activityTarget?.name}'s activity</DialogTitle>
          </DialogHeader>

          {loadingActivity ? (
            <p className="text-sm text-welljio-muted">Loading...</p>
          ) : activityData ? (
            <div className="space-y-5">
              <p className="text-xs text-welljio-subtle">Last {activityData.period_days} days</p>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-welljio-tint rounded-2xl p-4">
                  <div className="flex items-center gap-1.5 text-welljio-accent mb-1.5">
                    <Truck className="w-3.5 h-3.5" strokeWidth={1.8} />
                    <span className="text-[10px] uppercase tracking-widest font-semibold">Shipped</span>
                  </div>
                  <p className="font-heading text-2xl font-bold text-welljio-ink">{activityData.orders_shipped}</p>
                </div>
                <div className="bg-welljio-tint rounded-2xl p-4">
                  <div className="flex items-center gap-1.5 text-welljio-accent mb-1.5">
                    <Check className="w-3.5 h-3.5" strokeWidth={1.8} />
                    <span className="text-[10px] uppercase tracking-widest font-semibold">Delivered</span>
                  </div>
                  <p className="font-heading text-2xl font-bold text-welljio-ink">{activityData.orders_delivered}</p>
                </div>
                <div className="bg-welljio-tint rounded-2xl p-4">
                  <div className="flex items-center gap-1.5 text-welljio-accent mb-1.5">
                    <Clock className="w-3.5 h-3.5" strokeWidth={1.8} />
                    <span className="text-[10px] uppercase tracking-widest font-semibold">Avg. to ship</span>
                  </div>
                  {activityData.avg_hours_received_to_shipped !== null ? (
                    <p className={`font-heading text-2xl font-bold ${activityData.avg_hours_received_to_shipped <= activityData.sla_target_hours ? "text-welljio-ink" : "text-welljio-coral"}`}>
                      {activityData.avg_hours_received_to_shipped}h
                    </p>
                  ) : (
                    <p className="text-sm text-welljio-subtle">No data yet</p>
                  )}
                  <p className="text-[10px] text-welljio-subtle mt-0.5">Target: {activityData.sla_target_hours}h</p>
                </div>
                <div className="bg-welljio-tint rounded-2xl p-4">
                  <div className="flex items-center gap-1.5 text-welljio-accent mb-1.5">
                    <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.8} />
                    <span className="text-[10px] uppercase tracking-widest font-semibold">Returns</span>
                  </div>
                  <p className="font-heading text-2xl font-bold text-welljio-ink">{activityData.returns_processed}</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold text-welljio-ink mb-2 flex items-center gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" strokeWidth={1.8} /> Recent order actions
                </p>
                {activityData.recent_order_actions.length === 0 ? (
                  <p className="text-sm text-welljio-muted">No edits, cancellations, or refunds in this period.</p>
                ) : (
                  <ul className="space-y-2.5">
                    {activityData.recent_order_actions.map((entry) => (
                      <li key={entry.id} className="border-b border-slate-100 last:border-0 pb-2.5 last:pb-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-welljio-ink">{ACTION_LABEL[entry.action] || entry.action}</p>
                          <p className="text-xs text-welljio-subtle">{new Date(entry.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                        </div>
                        {entry.reason && <p className="text-xs text-welljio-muted mt-0.5">"{entry.reason}"</p>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
